import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Calendar,
  ExportSquare,
  Instagram,
  LogoutCurve,
  MessageNotif,
  ShieldTick,
  TickCircle,
  Warning2,
} from "iconsax-react";
import { useTranslation } from "react-i18next";

import { StatusBadge } from "@/components/Atoms";
import {
  ConfirmDialog,
  InstagramAvatar,
  InstagramInfoTile,
  SefanoButton,
} from "@/components/Molecules";
import type { InstagramAccountOverviewProps } from "./InstagramAccountOverview.types";
import * as styles from "./InstagramAccountOverview.styles";

export type { InstagramAccountOverviewProps } from "./InstagramAccountOverview.types";

export function InstagramAccountOverview({
  account,
  needsAttention,
  canManage,
  isRedirecting,
  onConnect,
  isResubscribing,
  onResubscribe,
  isDisconnectOpen,
  isDisconnecting,
  onDisconnectRequest,
  onDisconnectCancel,
  onDisconnectConfirm,
}: InstagramAccountOverviewProps) {
  const { t } = useTranslation("instagram");
  const theme = useTheme();

  const resubscribeAction =
    canManage && !needsAttention && !account.eventsActive ? (
      <SefanoButton
        size="small"
        disabled={isResubscribing}
        onClick={onResubscribe}
        sx={styles.smallAction}
      >
        {isResubscribing ? t("account.resubscribing") : t("account.resubscribe")}
      </SefanoButton>
    ) : null;

  return (
    <Box sx={styles.root}>
      <Box sx={styles.profileCard}>
        <Box sx={styles.avatarSlot}>
          <InstagramAvatar src={account.avatarUrl} alt={account.username} size={84} />
        </Box>
        <Box sx={styles.identity}>
          <Typography sx={styles.displayName}>{account.displayName}</Typography>
          <Box sx={styles.metaRow}>
            <Box
              component="a"
              href={account.profileUrl}
              target="_blank"
              rel="noopener noreferrer"
              dir="ltr"
              title={t("account.viewProfile")}
              sx={styles.username}
            >
              @{account.username}
              <ExportSquare size={14} variant="Linear" color="currentColor" />
            </Box>
            {account.accountTypeKey ? (
              <Box component="span" sx={styles.typeChip}>
                {t(account.accountTypeKey, { defaultValue: "" })}
              </Box>
            ) : null}
            <StatusBadge size="sm" color={account.statusColor} label={t(account.statusLabelKey)} />
          </Box>
        </Box>
      </Box>

      {needsAttention ? (
        <Box sx={styles.attention}>
          <Warning2 size={20} variant="Bold" color={theme.palette.warning.main} />
          <Box>
            <Typography sx={styles.attentionTitle}>{t("account.attentionTitle")}</Typography>
            <Typography sx={styles.attentionText}>{t(account.attentionDescriptionKey)}</Typography>
          </Box>
        </Box>
      ) : null}

      <Box sx={styles.tiles}>
        <InstagramInfoTile
          icon={MessageNotif}
          label={t("account.events")}
          value={account.eventsActive ? t("account.eventsActive") : t("account.eventsInactive")}
          tone={account.eventsActive ? "success" : "warning"}
          action={resubscribeAction}
        />
        <InstagramInfoTile
          icon={ShieldTick}
          label={t("account.token")}
          value={t("account.tokenValue", { date: account.tokenExpiresLabel })}
        />
        <InstagramInfoTile
          icon={Calendar}
          label={t("account.connectedAt")}
          value={account.connectedAtLabel}
        />
      </Box>

      <Box sx={styles.scopes}>
        <Typography sx={styles.sectionLabel}>{t("account.permissions")}</Typography>
        <Box sx={styles.scopeList}>
          {account.scopeKeys.map((scopeKey) => (
            <Box component="span" key={scopeKey} sx={styles.scopeChip}>
              <TickCircle size={14} variant="Bold" color="currentColor" />
              {t(scopeKey, { defaultValue: scopeKey.replace("account.scopes.", "") })}
            </Box>
          ))}
        </Box>
        {account.lastError && !account.eventsActive ? (
          <Typography sx={styles.lastError}>
            {t("account.lastError")} <bdi dir="ltr">{account.lastError}</bdi>
          </Typography>
        ) : null}
      </Box>

      {canManage ? (
        <Box sx={styles.footer}>
          <SefanoButton
            intent="danger"
            onClick={onDisconnectRequest}
            startIcon={<LogoutCurve size={18} variant="Linear" color={theme.palette.error.main} />}
            sx={styles.footerButton}
          >
            {t("account.disconnect")}
          </SefanoButton>
          {needsAttention ? (
            <SefanoButton
              onClick={onConnect}
              disabled={isRedirecting}
              startIcon={<Instagram size={18} variant="Bold" color={theme.palette.common.white} />}
              sx={styles.footerButton}
            >
              {isRedirecting ? t("hero.redirecting") : t("account.reconnect")}
            </SefanoButton>
          ) : null}
        </Box>
      ) : null}

      <ConfirmDialog
        open={isDisconnectOpen}
        title={t("disconnectDialog.title")}
        message={t("disconnectDialog.message", { username: account.username })}
        confirmLabel={t("disconnectDialog.confirm")}
        cancelLabel={t("disconnectDialog.cancel")}
        isPending={isDisconnecting}
        onConfirm={onDisconnectConfirm}
        onCancel={onDisconnectCancel}
      />
    </Box>
  );
}
